import { Container, FOCUS_RING_NO_RADIUS, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { ObjectivesLocationState } from '@/navigation/objectivesRoutes';

import { BackLink } from '@/components/BackLink';
import {
    GLASS_SURFACE,
    OBJECTIVES_PAGE_LAYOUT,
    TYPE_CHOICE_CARD,
} from '@/components/objectives/objectiveStyles';
import { useObjectivesStepBack } from '@/hooks/objectives/useObjectivesStepBack';
import { readPeriodParam } from '@/hooks/objectives/usePeriodOptions';
import { useNavigate } from '@/hooks/utils/useNavigate';
import {
    newObjectiveOfTypePath,
    objectivesPath,
    stepBackState,
} from '@/navigation/objectivesRoutes';

interface ObjectiveTypeOption {
    id: string;
    icon: 'cryptocompany-target' | 'users-people';
    titleKey: 'objectives.type.individual' | 'objectives.type.team';
    descriptionKey: 'objectives.type.individualDescription' | 'objectives.type.teamDescription';
}

const TYPE_OPTIONS: ObjectiveTypeOption[] = [
    {
        id: 'individual',
        icon: 'cryptocompany-target',
        titleKey: 'objectives.type.individual',
        descriptionKey: 'objectives.type.individualDescription',
    },
    {
        id: 'team',
        icon: 'users-people',
        titleKey: 'objectives.type.team',
        descriptionKey: 'objectives.type.teamDescription',
    },
];

export default function CreateObjectiveTypePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { canStepBack, stepBackOr } = useObjectivesStepBack();
    const { state: locationState, search } = useLocation<ObjectivesLocationState | undefined>();
    const period = readPeriodParam(search);

    return (
        <Container
            className={OBJECTIVES_PAGE_LAYOUT.shell}
            dataTestId={'create-objective-type-page'}
        >
            <BackLink
                onClick={() => stepBackOr({ path: objectivesPath({ period }) })}
                label={t('objectives.type.back')}
                dataTestId={'create-objective-type-back'}
                className={'mt-4 mb-6'}
            />

            <div className={'mt-2 flex flex-col gap-8 pb-24'}>
                <div
                    className={`${GLASS_SURFACE} w-full max-w-[480px] p-[37px_31px_36px_25px]`}
                    data-testid={'create-objective-type-header'}
                >
                    <div className={'flex items-center gap-3'}>
                        <div
                            className={
                                'flex h-[60px] w-[60px] flex-shrink-0 items-center justify-center rounded-full bg-[#EDF0F2]'
                            }
                        >
                            <Icon
                                name={'cryptocompany-target'}
                                size={36}
                                colorClassName={'text-obj-turquoise'}
                                strokeWidth={1.8}
                            />
                        </div>

                        <div className={'flex flex-col gap-[14px]'}>
                            <h1
                                className={
                                    'text-navy max-w-[318px] text-[24px] leading-[28px] font-medium'
                                }
                            >
                                {t('objectives.type.title')}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className={'obj-type-cards-row mt-10 items-center justify-center gap-6'}>
                    {TYPE_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            type={'button'}
                            // The form takes this choice's place: the screen the creation started
                            // from stays one step back, and the form's Retour reopens the choice.
                            onClick={() =>
                                navigate({
                                    path: newObjectiveOfTypePath({ type: option.id, period }),
                                    state: stepBackState({
                                        canStepBack,
                                        fromTypeChoice: true,
                                        forReader: locationState?.forReader,
                                    }),
                                    replace: true,
                                })
                            }
                            data-testid={`create-objective-type-${option.id}`}
                            className={`${TYPE_CHOICE_CARD} ${FOCUS_RING_NO_RADIUS} flex flex-col items-center justify-center gap-[36px] px-[58px]`}
                        >
                            <div
                                className={
                                    'flex h-[103px] w-[103px] flex-shrink-0 items-center justify-center rounded-[16px] bg-white/90'
                                }
                            >
                                <Icon
                                    name={option.icon}
                                    size={63}
                                    colorClassName={'text-obj-turquoise'}
                                    strokeWidth={1.5}
                                />
                            </div>

                            <div className={'flex w-full flex-col items-center gap-3'}>
                                <h2
                                    className={
                                        'text-navy text-center text-[24px] leading-[28px] font-medium'
                                    }
                                >
                                    {t(option.titleKey)}
                                </h2>

                                <p className={'text-navy text-center text-[14px] font-medium'}>
                                    {t(option.descriptionKey)}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </Container>
    );
}
